// CastLayoutView.tsx — Casting yard layout compositor.
// Layout: CastSidebar (draggable pick order) | Canvas | CastPropsPanel (settings right)

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useShallowStore } from '../store/useStore';
import CastSidebar, { type CastPanelItem } from './CastSidebar';
import CastLayoutCanvas from './CastLayoutCanvas';
import CastLayout3D from './CastLayout3D';
import CastPickAnimator from './CastPickAnimator';
import CastSimReport from './CastSimReport';
import ResizeHandle from './ResizeHandle';
import { computeFootprint, wallWidth, DIR_ANGLE } from '../utils/buildingFootprint';
import { cornerJointWidth } from '../types/panelvar';
import type {
  Elevation, PanelData,
  PanelOverride, PickAnimState, PickStep,
  SimulationResult, MonteCarloStats,
  SerializablePlacedPanel, SimWorkerRequest, SimWorkerResponse,
} from '../types/panelvar';
import { castLayout, type WallCastRow, type CastPlacedPanel } from '../utils/castLayout';
import { calcMaterials } from '../utils/materialsCalc';
import { computePanelInstalledPositions } from '../utils/installedPositions';
import { generatePickSteps, STEP_DURATION_MS } from '../utils/pickStepGen';

// ── Build slab corners from footprint ───────────────────────────────────────
function buildSlabCorners(
  floorElevs: Elevation[],
  allPanels: PanelData[],
  jointWidth: number,
): [number, number][] | null {
  if (floorElevs.length === 0) return null;
  const fp = computeFootprint(floorElevs, allPanels, jointWidth);
  if (!fp.isClosed || fp.corners.length < 3) return null;
  return fp.corners.map(c => [c.x, c.y] as [number, number]);
}

function buildFallbackCorners(
  floorElevs: Elevation[],
  allPanels: PanelData[],
  jointWidth: number,
): [number, number][] {
  const fp = computeFootprint(floorElevs, allPanels, jointWidth);
  let minX = 0, maxX = 0, minY = 0, maxY = 0;
  for (const w of fp.walls) {
    minX = Math.min(minX, w.startX, w.endX);
    maxX = Math.max(maxX, w.startX, w.endX);
    minY = Math.min(minY, w.startY, w.endY);
    maxY = Math.max(maxY, w.startY, w.endY);
  }
  const gap = 240;
  const resolvePanels = (e: Elevation) =>
    e.panelIds.map(id => allPanels.find(p => p.id === id)).filter((p): p is PanelData => p != null);
  for (const e of floorElevs) {
    if (fp.walls.find(w => w.elevId === e.id)) continue;
    const ep = resolvePanels(e);
    if (ep.length === 0) continue;
    const lj = cornerJointWidth(e.leftCorner ?? 'continuous', jointWidth);
    const rj = cornerJointWidth(e.rightCorner ?? 'continuous', jointWidth);
    const ww = wallWidth(ep, jointWidth, lj, rj);
    maxX = Math.max(maxX, minX + ww + gap);
    maxY = Math.max(maxY, minY + (Math.max(...ep.map(p => p.mpvar.height))) + gap);
  }
  const margin = 120;
  return [
    [minX - margin, minY - margin],
    [maxX + margin, minY - margin],
    [maxX + margin, maxY + margin],
    [minX - margin, maxY + margin],
  ];
}

function buildWallCastRows(
  floorElevs: Elevation[],
  allPanels: PanelData[],
  jointWidth: number,
): WallCastRow[] {
  if (floorElevs.length === 0) return [];
  const fp = computeFootprint(floorElevs, allPanels, jointWidth);
  const rows: WallCastRow[] = [];
  const visited = new Set<string>();

  const resolvePanels = (e: Elevation): PanelData[] =>
    e.panelIds.map(id => allPanels.find(p => p.id === id)).filter((p): p is PanelData => p != null);

  for (const w of fp.walls) {
    visited.add(w.elevId);
    rows.push({
      panels:      w.panels,
      startX:      w.startX,
      startZ:      w.startY,
      rotationRad: w.angle,
      leftJoint:   w.leftEndJoint,
      rightJoint:  w.rightEndJoint,
    });
  }

  let chainMinX = 0, chainMaxX = 0, chainMinZ = 0, chainMaxZ = 0;
  for (const w of fp.walls) {
    chainMinX = Math.min(chainMinX, w.startX, w.endX);
    chainMaxX = Math.max(chainMaxX, w.startX, w.endX);
    chainMinZ = Math.min(chainMinZ, w.startY, w.endY);
    chainMaxZ = Math.max(chainMaxZ, w.startY, w.endY);
  }
  const chainMidX = (chainMinX + chainMaxX) / 2;
  const chainMidZ = (chainMinZ + chainMaxZ) / 2;
  let standaloneOffset = 0;
  const gap = 240;

  for (const e of floorElevs) {
    if (visited.has(e.id)) continue;
    const ep = resolvePanels(e);
    if (ep.length === 0) continue;
    const lj = cornerJointWidth(e.leftCorner ?? 'continuous', jointWidth);
    const rj = cornerJointWidth(e.rightCorner ?? 'continuous', jointWidth);
    const ww = wallWidth(ep, jointWidth, lj, rj);
    let sx: number, sz: number, srot: number;
    if (e.worldX !== undefined && e.worldZ !== undefined) {
      sx = e.worldX; sz = e.worldZ;
      srot = e.worldRotation ?? (DIR_ANGLE[e.direction] ?? 0);
    } else {
      srot = DIR_ANGLE[e.direction] ?? 0;
      switch (e.direction) {
        case 'R': sx = chainMaxX + gap;    sz = chainMidZ - ww / 2; break;
        case 'L': sx = chainMinX - gap;    sz = chainMidZ + ww / 2; break;
        case 'B': sx = chainMidX - ww / 2; sz = chainMinZ - gap;    break;
        case 'F': sx = chainMidX + ww / 2; sz = chainMaxZ + gap;    break;
        default:  sx = chainMaxX + gap + standaloneOffset; sz = chainMidZ; break;
      }
      standaloneOffset += ww + gap;
    }
    rows.push({ panels: ep, startX: sx, startZ: sz, rotationRad: srot, leftJoint: lj, rightJoint: rj });
  }
  return rows;
}

// ── Row-packing helpers ───────────────────────────────────────────────────────

/** Axis-aligned bounding box width of a panel (accounts for rotation). */
function effectiveW(pp: CastPlacedPanel): number {
  return Math.abs(Math.cos(pp.rotationRad)) * pp.w + Math.abs(Math.sin(pp.rotationRad)) * pp.h;
}

/** Axis-aligned bounding box height of a panel (accounts for rotation). */
function effectiveH(pp: CastPlacedPanel): number {
  return Math.abs(Math.sin(pp.rotationRad)) * pp.w + Math.abs(Math.cos(pp.rotationRad)) * pp.h;
}

/**
 * Extent of panel along an arbitrary plan axis defined by axisRot.
 * axisRot = 0 → drawing X (same as effectiveW).
 * axisRot = π/2 → drawing Y.
 * For any other angle this gives the correct half-AABB projection.
 */
function effectiveAlongAxis(pp: CastPlacedPanel, axisRot: number): number {
  const rel = pp.rotationRad - axisRot;
  return Math.abs(Math.cos(rel)) * pp.w + Math.abs(Math.sin(rel)) * pp.h;
}

/**
 * Group panels into horizontal rows by Y-proximity.
 * When majorBoxIn > 0 two hard constraints apply:
 *   1. Same major Y box  — panels cannot span a major Y gridline.
 *   2. X-proximity       — the candidate must be within one major box width
 *      of the nearest existing panel in the row, preventing a dropped panel
 *      from being pulled across the site to join a distant same-Y row.
 */
function groupIntoRows(panels: CastPlacedPanel[], majorBoxIn = 0): CastPlacedPanel[][] {
  if (panels.length === 0) return [];
  const tol = Math.max(...panels.map(effectiveH)) * 0.55;
  const rows: CastPlacedPanel[][] = [];
  for (const p of panels) {
    const match = rows.find(row => {
      const rowY = row.reduce((s, r) => s + r.cy, 0) / row.length;
      if (Math.abs(p.cy - rowY) > tol) return false;
      if (majorBoxIn > 0) {
        if (Math.floor(p.cy / majorBoxIn) !== Math.floor(rowY / majorBoxIn)) return false;
        const nearestX = Math.min(...row.map(r => Math.abs(r.cx - p.cx)));
        if (nearestX > majorBoxIn * 0.3) return false;
      }
      return true;
    });
    if (match) match.push(p);
    else rows.push([p]);
  }
  return rows;
}

/**
 * Repack all rows so panels are gap-separated with no overlaps.
 *
 * Snapped rows (containing a manually-placed panel):
 *   - Anchor = first locked panel when sorted along the row's own axis.
 *   - The row axis is the anchor panel's local width direction so that
 *     panels rotated 90°/270° pack along drawing Y, not drawing X.
 *   - Anchor position snaps to nearest minor gridline on both axes.
 *   - All other panels pack flush from anchor edges along the same axis.
 *
 * Un-snapped rows use the original bounding-box-center pack in drawing X.
 *
 * @param snapGridIn  Minor grid size in plan inches (120 = 10 ft).
 */
function repackRows(
  panels: CastPlacedPanel[],
  gapIn: number,
  overriddenIds: ReadonlySet<string> = new Set(),
  snapGridIn = 0,
  majorBoxIn = 0,
): Map<string, { cx: number; cy: number }> {
  const rows = groupIntoRows(panels, majorBoxIn);
  const result = new Map<string, { cx: number; cy: number }>();

  for (const row of rows) {
    const shouldSnap = snapGridIn > 0 && row.some(p => overriddenIds.has(p.panel.id));

    if (shouldSnap) {
      // Anchor = first locked panel; it defines the row's packing axis.
      const anchor = row.find(p => overriddenIds.has(p.panel.id)) ?? row[0];
      const rowRot  = anchor.rotationRad;

      // Unit vectors: along-panel (ax, ay) and perpendicular (px, py) in plan XY.
      const ax =  Math.cos(rowRot), ay =  Math.sin(rowRot);
      const px = -Math.sin(rowRot), py =  Math.cos(rowRot);

      // Projection helpers
      const projAlong = (p: CastPlacedPanel) => p.cx * ax + p.cy * ay;
      const projPerp  = (p: CastPlacedPanel) => p.cx * px + p.cy * py;

      // Sort panels along the row axis (negative-projection end = "left of panel")
      const sorted = [...row].sort((a, b) => projAlong(a) - projAlong(b));
      const anchorIdx = sorted.indexOf(anchor);

      // Snap anchor to nearest minor gridline along both axes
      const anchorAlong = Math.round(projAlong(anchor) / snapGridIn) * snapGridIn;
      const anchorPerp  = Math.round(projPerp(anchor)  / snapGridIn) * snapGridIn;

      // Reconstruct (cx, cy) from (along, perp) coordinates
      const toCxCy = (a: number, p: number) => ({
        cx: a * ax + p * px,
        cy: a * ay + p * py,
      });

      result.set(anchor.panel.id, toCxCy(anchorAlong, anchorPerp));

      // Pack toward negative axis (panels before anchor)
      let negEdge = anchorAlong - effectiveAlongAxis(anchor, rowRot) / 2;
      for (let i = anchorIdx - 1; i >= 0; i--) {
        const p = sorted[i];
        const ew = effectiveAlongAxis(p, rowRot);
        const a  = negEdge - gapIn - ew / 2;
        result.set(p.panel.id, toCxCy(a, anchorPerp));
        negEdge = a - ew / 2;
      }

      // Pack toward positive axis (panels after anchor)
      let posEdge = anchorAlong + effectiveAlongAxis(anchor, rowRot) / 2;
      for (let i = anchorIdx + 1; i < sorted.length; i++) {
        const p = sorted[i];
        const ew = effectiveAlongAxis(p, rowRot);
        const a  = posEdge + gapIn + ew / 2;
        result.set(p.panel.id, toCxCy(a, anchorPerp));
        posEdge = a + ew / 2;
      }
    } else {
      // Un-snapped: preserve bounding-box center X, pack flush in drawing X
      const sortedX = [...row].sort((a, b) => a.cx - b.cx);
      const rawY    = sortedX.reduce((s, p) => s + p.cy, 0) / sortedX.length;
      const leftEdge  = Math.min(...sortedX.map(p => p.cx - effectiveW(p) / 2));
      const rightEdge = Math.max(...sortedX.map(p => p.cx + effectiveW(p) / 2));
      const centerX   = (leftEdge + rightEdge) / 2;
      const totalW    = sortedX.reduce((s, p) => s + effectiveW(p), 0) + gapIn * (sortedX.length - 1);
      let x = centerX - totalW / 2;
      for (const p of sortedX) {
        const ew = effectiveW(p);
        result.set(p.panel.id, { cx: x + ew / 2, cy: rawY });
        x += ew + gapIn;
      }
    }
  }

  return result;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CastLayoutView() {
  const {
    project,
    ui,
    castLayoutSettings,
    castViewMode,
    toggleSidebar,
    toggleProps,
    setCastLayoutSettings,
  } = useShallowStore(s => ({
    project:            s.project,
    ui:                 s.ui,
    castLayoutSettings: s.ui.castLayoutSettings,
    castViewMode:       s.ui.castViewMode,
    toggleSidebar:      s.toggleSidebar,
    toggleProps:        s.toggleProps,
    setCastLayoutSettings: s.setCastLayoutSettings,
  }));

  const [activeCastFloorId, setActiveCastFloorId] = useState<string | null>(null);
  const [sidebarW, setSidebarW] = useState(() => Math.round(window.innerWidth * 0.18));
  const [propsW,   setPropsW]   = useState(() => Math.round(window.innerWidth * 0.2));
  const onSidebarResize = useCallback((dx: number) => setSidebarW(w => Math.max(80, Math.min(500, w + dx))), []);
  const onPropsResize   = useCallback((dx: number) => setPropsW(w => Math.max(80, Math.min(500, w - dx))), []);

  // Panel exclusion and pick-order state
  const [checkedIds,     setCheckedIds]     = useState<Set<string>>(new Set());
  const [panelPickOrder, setPanelPickOrder] = useState<string[] | null>(null);

  // Feature 1: drag overrides
  const [panelOverrides,   setPanelOverrides]   = useState<Map<string, PanelOverride>>(new Map());
  const [selectedPanelIds, setSelectedPanelIds] = useState<Set<string>>(new Set());

  const handleRubberBandSelect = useCallback((ids: string[]) => {
    setSelectedPanelIds(new Set(ids));
  }, []);

  // Feature 2: install arrows
  const [showInstallArrows, setShowInstallArrows] = useState(false);

  // Feature 3: animation
  const [showAnimation, setShowAnimation] = useState(false);
  const [animState, setAnimState]         = useState<PickAnimState>({
    isPlaying: false, currentStep: 0, speed: 1, direction: 1,
  });
  const frameRef = useRef<number | null>(null);
  const lastStepTimeRef = useRef<number>(0);

  // Feature 4: simulation
  const [showSimReport, setShowSimReport] = useState(false);
  const [simResults,    setSimResults]    = useState<SimulationResult[]>([]);
  const [mcStats,       setMcStats]       = useState<MonteCarloStats | null>(null);
  const [simRunning,    setSimRunning]    = useState(false);
  const [simError,      setSimError]      = useState<string | null>(null);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    return () => { workerRef.current?.terminate(); };
  }, []);

  if (!project) return null;

  const allPanels     = project.panels;
  const allElevations = project.elevations ?? [];
  const allFloors     = project.floors ?? [];
  const jointWidth    = project.settings.jointWidth ?? 0.75;

  if (allFloors.length === 0 || allPanels.length === 0) {
    return (
      <div className="site-view" style={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <div style={{ textAlign: 'center', maxWidth: 480, padding: 32 }}>
          <h2 style={{ marginBottom: 12 }}>Cast Layout</h2>
          <p style={{ color: 'var(--text-muted, #888)', lineHeight: 1.5 }}>
            {allFloors.length === 0
              ? 'Add a floor and place panels in Building mode to lay out the cast yard.'
              : 'Place panels on elevations to lay out the cast yard.'}
          </p>
        </div>
      </div>
    );
  }

  const activeFloor = allFloors.find(f => f.id === activeCastFloorId) ?? allFloors[0] ?? null;

  const { wallRows, floorPanels, slabCorners, totalWeightTons, floorElevs } = useMemo(() => {
    if (!activeFloor) return {
      wallRows: [] as WallCastRow[],
      floorPanels: [] as PanelData[],
      slabCorners: [] as [number, number][],
      totalWeightTons: 0,
      floorElevs: [] as Elevation[],
    };

    const floorElevIds = new Set(activeFloor.elevationIds);
    const elevs        = allElevations.filter(e => floorElevIds.has(e.id));

    const panels = elevs
      .flatMap(e => e.panelIds.map(id => allPanels.find(p => p.id === id)))
      .filter((p): p is PanelData => p != null);

    const corners =
      buildSlabCorners(elevs, allPanels, jointWidth) ??
      buildFallbackCorners(elevs, allPanels, jointWidth);

    const rows = buildWallCastRows(elevs, allPanels, jointWidth);
    const tons = panels.reduce((sum, p) => sum + calcMaterials(p).panelWeightTons, 0);

    return { wallRows: rows, floorPanels: panels, slabCorners: corners, totalWeightTons: tons, floorElevs: elevs };
  }, [activeFloor, allFloors, allElevations, allPanels, jointWidth]);

  // Reset pick order and checked state when floor changes
  useEffect(() => {
    setCheckedIds(new Set(floorPanels.map(p => p.id)));
    setPanelPickOrder(null);
    setPanelOverrides(new Map());
    setSelectedPanelIds(new Set());
  }, [activeFloor?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wall rows filtered to only checked panels
  const filteredWallRows = useMemo(() =>
    wallRows
      .map(wall => ({ ...wall, panels: wall.panels.filter(p => checkedIds.has(p.id)) }))
      .filter(w => w.panels.length > 0),
    [wallRows, checkedIds],
  );

  // Pick order override: checked panels in user-defined order
  const checkedPickOrder = useMemo(() => {
    if (!panelPickOrder) return undefined;
    return panelPickOrder.filter(id => checkedIds.has(id));
  }, [panelPickOrder, checkedIds]);

  const castResult = useMemo(
    () => castLayout(filteredWallRows, slabCorners, castLayoutSettings, checkedPickOrder),
    [filteredWallRows, slabCorners, castLayoutSettings, checkedPickOrder],
  );

  // Seed panelPickOrder from the perimeter-walk order returned by castLayout
  useEffect(() => {
    if (panelPickOrder === null && castResult.perimOrder.length > 0) {
      setPanelPickOrder(castResult.perimOrder);
    }
  }, [castResult.perimOrder, panelPickOrder]);

  // Feature 1: merge position overrides into placed result (no algorithm re-run)
  const displayedPlaced = useMemo((): CastPlacedPanel[] =>
    castResult.placed.map(pp => {
      const ov = panelOverrides.get(pp.panel.id);
      if (!ov) return pp;
      return {
        ...pp,
        cx: ov.cx, cy: ov.cy, rotationRad: ov.rotationRad,
        pickRadiusIn: Math.hypot(ov.cx - castResult.craneX, ov.cy - castResult.craneY),
      };
    }),
    [castResult, panelOverrides],
  );

  const displayResult = useMemo(
    () => ({ ...castResult, placed: displayedPlaced }),
    [castResult, displayedPlaced],
  );

  // Stable refs for drag callbacks (assigned during render; avoids stale closures in DOM handlers)
  const castResultRef      = useRef(castResult);
  const displayedPlacedRef = useRef(displayedPlaced);
  const gapInRef           = useRef(castLayoutSettings.panelGapIn);
  castResultRef.current      = castResult;
  displayedPlacedRef.current = displayedPlaced;
  gapInRef.current           = castLayoutSettings.panelGapIn;

  const selectedPanelIdsRef = useRef(selectedPanelIds);
  selectedPanelIdsRef.current = selectedPanelIds;

  // Feature 2 + 3: installed positions (computed when needed by arrows or animation)
  const needsInstalledPositions = showInstallArrows || showAnimation;
  const installedPositions = useMemo(() => {
    if (!needsInstalledPositions || floorElevs.length === 0) return null;
    return computePanelInstalledPositions(floorElevs, allPanels, jointWidth);
  }, [needsInstalledPositions, floorElevs, allPanels, jointWidth]);

  // Feature 3: pick steps
  const pickSteps = useMemo((): PickStep[] => {
    if (!showAnimation || !installedPositions) return [];
    return generatePickSteps(
      displayedPlaced, castResult.craneX, castResult.craneY, installedPositions,
    );
  }, [showAnimation, displayedPlaced, castResult.craneX, castResult.craneY, installedPositions]);

  // Feature 3: RAF playback loop
  useEffect(() => {
    if (!showAnimation || !animState.isPlaying) {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      return;
    }
    const stepMs = STEP_DURATION_MS / animState.speed;
    lastStepTimeRef.current = 0;
    function tick(now: number) {
      if (lastStepTimeRef.current === 0) lastStepTimeRef.current = now;
      if (now - lastStepTimeRef.current >= stepMs) {
        lastStepTimeRef.current = now;
        setAnimState(prev => {
          const next = prev.currentStep + prev.direction;
          if (next < 0 || next >= pickSteps.length) return { ...prev, isPlaying: false };
          return { ...prev, currentStep: next };
        });
      }
      frameRef.current = requestAnimationFrame(tick);
    }
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [showAnimation, animState.isPlaying, animState.speed, animState.direction, pickSteps.length]);

  // Reset animation when steps change
  useEffect(() => {
    setAnimState(prev => ({ ...prev, isPlaying: false, currentStep: 0 }));
  }, [pickSteps.length]);

  // Feature 4: run simulation in worker
  function runSimulation() {
    if (simRunning) return;
    setSimError(null);
    setSimRunning(true);
    setShowSimReport(true);

    const simInstalledPos = computePanelInstalledPositions(floorElevs, allPanels, jointWidth);
    const serializablePanels: SerializablePlacedPanel[] = displayedPlaced.map(pp => {
      const picks = pp.panel.ppvar.filter(s => s.enabled);
      const pickSpread = picks.length < 2 ? 0 : Math.max(
        ...picks.flatMap((a, i) =>
          picks.slice(i + 1).map(b => Math.hypot(a.data.x - b.data.x, a.data.y - b.data.y)),
        ),
      );
      const braceCount = pp.panel.bpvar.filter(s => s.enabled).length;
      const inst = simInstalledPos.get(pp.panel.id);
      return {
        panelId:      pp.panel.id,
        panelMark:    pp.panel.mpvar.panelMark,
        cx:           pp.cx,
        cy:           pp.cy,
        weightTons:   pp.weightTons,
        pickRadiusIn: pp.pickRadiusIn,
        pickCount:    picks.length,
        pickSpread,
        braceCount,
        pickOrder:    pp.pickOrder,
        installedX:   inst?.x ?? pp.cx,
        installedY:   inst?.y ?? pp.cy,
      };
    });

    const req: SimWorkerRequest = {
      placed:           serializablePanels,
      craneX:           castResult.craneX,
      craneY:           castResult.craneY,
      craneExclusionIn: castLayoutSettings.craneExclusionIn,
      mcIterations:     500,
      perimOrder:       castResult.perimOrder,
    };

    let worker: Worker;
    try {
      worker = new Worker(
        new URL('../workers/pickSimWorker.ts', import.meta.url),
        { type: 'module' },
      );
    } catch (err) {
      Sentry.captureException(err, { tags: { source: 'cast-sim-worker', phase: 'spawn' } });
      setSimRunning(false);
      setSimResults([]);
      setMcStats(null);
      setSimError(`Failed to start simulation worker: ${err instanceof Error ? err.message : String(err)}`);
      return;
    }
    workerRef.current = worker;
    worker.onmessage = (e: MessageEvent<SimWorkerResponse>) => {
      if (!e.data || !Array.isArray(e.data.results)) {
        Sentry.captureMessage('cast-sim worker returned no results', {
          level: 'error',
          tags: { source: 'cast-sim-worker', phase: 'no-results' },
        });
        setSimError('Simulation returned no results.');
        setSimResults([]);
        setMcStats(null);
        setSimRunning(false);
        worker.terminate();
        workerRef.current = null;
        return;
      }
      setSimResults(e.data.results);
      setMcStats(e.data.mcStats);
      setSimRunning(false);
      worker.terminate();
      workerRef.current = null;
    };
    worker.onerror = (e: ErrorEvent) => {
      Sentry.captureException(e.error ?? new Error(e.message || 'cast-sim worker error'), {
        tags: { source: 'cast-sim-worker', phase: 'onerror' },
        extra: { filename: e.filename, lineno: e.lineno, colno: e.colno },
      });
      setSimRunning(false);
      setSimResults([]);
      setMcStats(null);
      setSimError(`Simulation worker error: ${e.message || 'unknown error'}`);
      worker.terminate();
      workerRef.current = null;
    };
    worker.onmessageerror = () => {
      Sentry.captureMessage('cast-sim worker sent unreadable message', {
        level: 'error',
        tags: { source: 'cast-sim-worker', phase: 'onmessageerror' },
      });
      setSimRunning(false);
      setSimResults([]);
      setMcStats(null);
      setSimError('Simulation worker sent an unreadable message (data corruption).');
      worker.terminate();
      workerRef.current = null;
    };
    worker.postMessage(req);
  }

  const handleApplyPickPlan = useCallback((order: string[]) => {
    setPanelPickOrder(order);
    setShowSimReport(false);
  }, []);

  // Feature 1: panel drag — free movement; repack on drop (stable deps: no displayedPlaced)
  const handlePanelMove = useCallback((panelId: string, cx: number, cy: number) => {
    setPanelOverrides(m => {
      const displayed = displayedPlacedRef.current;
      const panel = displayed.find(p => p.panel.id === panelId);
      // Compute delta from current displayed position
      const oldCx = m.get(panelId)?.cx ?? panel?.cx ?? cx;
      const oldCy = m.get(panelId)?.cy ?? panel?.cy ?? cy;
      const dx = cx - oldCx;
      const dy = cy - oldCy;

      const next = new Map(m);
      const existingRot = m.get(panelId)?.rotationRad ?? panel?.rotationRad ?? 0;
      next.set(panelId, { cx, cy, rotationRad: existingRot });

      // Move all other selected panels by the same delta
      for (const id of selectedPanelIdsRef.current) {
        if (id === panelId) continue;
        const pp = displayed.find(p => p.panel.id === id);
        if (!pp) continue;
        const ov = m.get(id);
        next.set(id, {
          cx: (ov?.cx ?? pp.cx) + dx,
          cy: (ov?.cy ?? pp.cy) + dy,
          rotationRad: ov?.rotationRad ?? pp.rotationRad ?? 0,
        });
      }
      return next;
    });
  }, []);

  const handlePanelRotate = useCallback((panelId: string, newRotRad: number) => {
    setPanelOverrides(current => {
      const placed = displayedPlacedRef.current;
      const { craneX, craneY } = castResultRef.current;
      const pp = placed.find(p => p.panel.id === panelId);
      if (!pp) return current;

      const next = new Map(current);
      const ov = current.get(panelId);
      next.set(panelId, {
        cx: ov?.cx ?? pp.cx,
        cy: ov?.cy ?? pp.cy,
        rotationRad: newRotRad,
      });

      // Repack the panel's row to prevent overlaps after rotation
      const isSnapped = next.has(panelId);
      const cur: CastPlacedPanel[] = placed.map(p => {
        const o = next.get(p.panel.id);
        if (!o) return p;
        return { ...p, cx: o.cx, cy: o.cy, rotationRad: o.rotationRad,
          pickRadiusIn: Math.hypot(o.cx - craneX, o.cy - craneY) };
      });
      const overriddenIds = new Set(next.keys());
      const newPos = repackRows(cur, gapInRef.current, overriddenIds, isSnapped ? 120 : 0, isSnapped ? 1200 : 0);
      const rows = groupIntoRows(cur, 1200);
      const panelRow = rows.find(row => row.some(p => p.panel.id === panelId));
      if (panelRow) {
        for (const p of panelRow) {
          const pos = newPos.get(p.panel.id);
          if (!pos) continue;
          const o = next.get(p.panel.id);
          next.set(p.panel.id, {
            cx: pos.cx, cy: pos.cy,
            rotationRad: o?.rotationRad ?? p.rotationRad,
          });
        }
      }
      return next;
    });
  }, []);

  // On drop: snap the dropped panel and its row to major grid box centers (100-ft boxes).
  // Only panels in rows that contain a manually-placed panel get locked to grid boxes.
  // Untouched rows stay at their algorithm positions.
  const handlePanelDrop = useCallback((panelId: string) => {
    setPanelOverrides(currentOverrides => {
      const { placed, craneX, craneY } = castResultRef.current;
      const cur: CastPlacedPanel[] = placed.map(pp => {
        const ov = currentOverrides.get(pp.panel.id);
        if (!ov) return pp;
        return { ...pp, cx: ov.cx, cy: ov.cy, rotationRad: ov.rotationRad,
          pickRadiusIn: Math.hypot(ov.cx - craneX, ov.cy - craneY) };
      });
      const overriddenIds = new Set([...currentOverrides.keys(), panelId]);
      const newPos = repackRows(cur, gapInRef.current, overriddenIds, 120, 1200);

      // Only lock panels in rows that contain at least one overridden panel
      const touchedIds = new Set<string>();
      for (const row of groupIntoRows(cur, 1200)) {
        if (row.some(p => overriddenIds.has(p.panel.id))) {
          row.forEach(p => touchedIds.add(p.panel.id));
        }
      }

      const next = new Map<string, PanelOverride>(currentOverrides);
      for (const pp of cur) {
        if (!touchedIds.has(pp.panel.id)) continue;
        const pos = newPos.get(pp.panel.id);
        const ov  = currentOverrides.get(pp.panel.id);
        next.set(pp.panel.id, {
          cx: pos?.cx ?? pp.cx,
          cy: pos?.cy ?? pp.cy,
          rotationRad: ov?.rotationRad ?? pp.rotationRad,
        });
      }
      return next;
    });
    // Update pick order: row-by-row top→bottom, left→right within rows
    setPanelPickOrder(
      [...displayedPlacedRef.current].sort((a, b) => {
        const tol = Math.max(a.h, b.h) * 0.6;
        const dy  = a.cy - b.cy;
        return Math.abs(dy) <= tol ? a.cx - b.cx : dy;
      }).map(p => p.panel.id),
    );
  }, []);

  // Lookup maps for sidebar items
  const { panelById, panelElevName } = useMemo(() => {
    const byId   = new Map(floorPanels.map(p => [p.id, p]));
    const elvMap = new Map<string, string>();
    for (const e of floorElevs) {
      for (const id of e.panelIds) elvMap.set(id, e.name);
    }
    return { panelById: byId, panelElevName: elvMap };
  }, [floorPanels, floorElevs]);

  const sidebarItems = useMemo((): CastPanelItem[] => {
    if (!panelPickOrder) return [];
    return panelPickOrder
      .filter(id => panelById.has(id))
      .map(id => ({ panel: panelById.get(id)!, elevName: panelElevName.get(id) ?? '' }));
  }, [panelPickOrder, panelById, panelElevName]);

  const stackCount = castResult.placed.filter(p => p.isStacked).length;

  // Checkbox handlers
  const handleToggle = useCallback((id: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll   = useCallback(() => setCheckedIds(new Set(floorPanels.map(p => p.id))), [floorPanels]);
  const handleDeselectAll = useCallback(() => setCheckedIds(new Set()), []);
  const handleReorder     = useCallback((newOrder: string[]) => setPanelPickOrder(newOrder), []);

  const currentPickStep = showAnimation ? (pickSteps[animState.currentStep] ?? null) : null;

  return (
    <div className="site-view" style={{ background: '#1a1f2e' }}>
      {/* ── Left sidebar ─────────────────────────────────────────────────────── */}
      <aside
        className={`sidebar${ui.sidebarOpen ? '' : ' collapsed'}`}
        style={ui.sidebarOpen ? { width: sidebarW, minWidth: sidebarW } : undefined}
      >
        {allFloors.length > 1 && ui.sidebarOpen && (
          <div className="floor-switcher">
            {allFloors.map(f => (
              <button
                key={f.id}
                className={`floor-tab${f.id === activeFloor?.id ? ' active' : ''}`}
                onClick={() => setActiveCastFloorId(f.id)}
                title={f.name}
              >
                {f.name}
              </button>
            ))}
          </div>
        )}

        <CastSidebar
          items={sidebarItems}
          checkedIds={checkedIds}
          onReorder={handleReorder}
          onToggle={handleToggle}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          collapsed={!ui.sidebarOpen}
          onToggleCollapse={toggleSidebar}
        />

        {ui.sidebarOpen && (
          <div style={{ padding: '8px 12px', borderTop: '1px solid #2a3a4a', fontSize: 12, color: '#aaa' }}>
            <div>{checkedIds.size} / {floorPanels.length} panels · {totalWeightTons.toFixed(1)} T total</div>
            {stackCount > 0 && (
              <div style={{ color: '#f0a030', marginTop: 4 }}>⚠ {stackCount} panels stacked</div>
            )}
          </div>
        )}
      </aside>

      {ui.sidebarOpen && <ResizeHandle onResize={onSidebarResize} />}

      {/* ── Canvas ──────────────────────────────────────────────────────────── */}
      <div className="site-view-viewport" style={{ position: 'relative', overflow: 'hidden' }}>
        {floorPanels.length === 0 ? (
          <div className="viewer-no-panel">No panels on this floor.</div>
        ) : slabCorners.length < 3 ? (
          <div className="viewer-no-panel">
            Building footprint unavailable — connect elevations to form a closed loop.
          </div>
        ) : castViewMode === '3d' ? (
          <CastLayout3D
            result={displayResult}
            slabCorners={slabCorners}
            settings={castLayoutSettings}
            currentPickStep={showAnimation ? (pickSteps[animState.currentStep] ?? null) : null}
            allPickSteps={showAnimation ? pickSteps : undefined}
            installedPositions={installedPositions ?? undefined}
            selectedPanelIds={selectedPanelIds}
            onPanelSelect={(id) => setSelectedPanelIds(id ? new Set([id]) : new Set())}
            onRubberBandSelect={handleRubberBandSelect}
            onPanelMove={handlePanelMove}
            onPanelRotate={handlePanelRotate}
            onPanelDrop={handlePanelDrop}
          />
        ) : (
          <CastLayoutCanvas
            result={displayResult}
            slabCorners={slabCorners}
            settings={castLayoutSettings}
            installedPositions={installedPositions}
            selectedPanelIds={selectedPanelIds}
            onPanelSelect={(id) => setSelectedPanelIds(id ? new Set([id]) : new Set())}
            onRubberBandSelect={handleRubberBandSelect}
            onPanelMove={handlePanelMove}
            onPanelRotate={handlePanelRotate}
            onPanelDrop={handlePanelDrop}
            currentPickStep={currentPickStep}
            allPickSteps={pickSteps}
          />
        )}

        {/* Feature 3: animation toolbar */}
        {showAnimation && pickSteps.length > 0 && (
          <CastPickAnimator
            steps={pickSteps}
            animState={animState}
            onSetState={patch => setAnimState(prev => ({ ...prev, ...patch }))}
          />
        )}

        {/* Feature 4: simulation report modal */}
        {showSimReport && mcStats && (
          <CastSimReport
            results={simResults}
            mcStats={mcStats}
            isRunning={simRunning}
            onApply={handleApplyPickPlan}
            onClose={() => setShowSimReport(false)}
          />
        )}
      </div>

      {ui.propsOpen && <ResizeHandle onResize={onPropsResize} />}

      {/* ── Right props panel ────────────────────────────────────────────────── */}
      <aside
        className={`props-panel${ui.propsOpen ? '' : ' collapsed'}`}
        style={ui.propsOpen ? { width: propsW, minWidth: propsW } : undefined}
      >
        {!ui.propsOpen ? (
          <button
            className="props-panel-toggle"
            onClick={toggleProps}
            title="Show settings"
          >
            ◀
          </button>
        ) : (
          <div className="props-panel-inner">
            <div className="props-panel-header">
              <span className="props-panel-title">Layout Settings</span>
              <button className="props-panel-collapse-btn" onClick={toggleProps} title="Hide panel">▶</button>
            </div>

            <div className="props-section">
              <label className="props-row" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={castLayoutSettings.showCapacityRings}
                  onChange={e => setCastLayoutSettings({ showCapacityRings: e.target.checked })}
                />
                <span className="props-label" style={{ marginLeft: 6 }}>Capacity rings</span>
              </label>

              {/* Feature 2: install arrows toggle */}
              <label className="props-row" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showInstallArrows}
                  onChange={e => setShowInstallArrows(e.target.checked)}
                />
                <span className="props-label" style={{ marginLeft: 6 }}>Install arrows</span>
              </label>

              {/* Feature 3: animation toggle */}
              <label className="props-row" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showAnimation}
                  onChange={e => {
                    setShowAnimation(e.target.checked);
                    if (!e.target.checked) setAnimState(prev => ({ ...prev, isPlaying: false }));
                  }}
                />
                <span className="props-label" style={{ marginLeft: 6 }}>Pick animation</span>
              </label>

              <div className="props-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <label className="props-label">Pick order</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: '100%' }}>
                  {([
                    { label: 'Project', value: 'project' },
                    { label: 'Heaviest', value: 'weight-desc' },
                    { label: 'Lightest', value: 'weight-asc' },
                  ] as const).map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setCastLayoutSettings({ sortBy: opt.value });
                        setPanelPickOrder(null);
                        setPanelOverrides(new Map());
                      }}
                      style={{
                        flex: '1 1 auto', padding: '4px 6px', fontSize: 11,
                        background: castLayoutSettings.sortBy === opt.value ? '#1a3a5a' : '#1a2a3a',
                        border: `1px solid ${castLayoutSettings.sortBy === opt.value ? '#4da6ff' : '#2a3a4a'}`,
                        borderRadius: 3, color: castLayoutSettings.sortBy === opt.value ? '#4da6ff' : '#88aacc',
                        cursor: 'pointer',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => { setPanelPickOrder(null); setPanelOverrides(new Map()); }}
                  style={{
                    width: '100%', padding: '4px 6px', fontSize: 11,
                    background: '#2a1a1a', border: '1px solid #4a2a2a',
                    borderRadius: 3, color: '#f0a030', cursor: 'pointer',
                  }}
                >
                  Reset layout
                </button>
              </div>

              <div className="props-row">
                <label className="props-label">Gap</label>
                <div className="props-input-wrap" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="number"
                    className="props-input"
                    value={castLayoutSettings.panelGapIn}
                    min={6} max={48} step={1}
                    onChange={e => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v)) setCastLayoutSettings({ panelGapIn: Math.max(6, Math.min(48, v)) });
                    }}
                    style={{ width: 64 }}
                  />
                  <span className="props-unit">in</span>
                </div>
              </div>

              <div className="props-row">
                <label className="props-label">Wall clearance</label>
                <div className="props-input-wrap" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="number"
                    className="props-input"
                    value={castLayoutSettings.clearanceIn}
                    min={12} max={120} step={6}
                    onChange={e => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v)) setCastLayoutSettings({ clearanceIn: Math.max(12, Math.min(120, v)) });
                    }}
                    style={{ width: 64 }}
                  />
                  <span className="props-unit">in</span>
                </div>
              </div>

              <div className="props-row">
                <label className="props-label">Crane excl.</label>
                <div className="props-input-wrap" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="number"
                    className="props-input"
                    value={castLayoutSettings.craneExclusionIn}
                    min={120} max={960} step={24}
                    onChange={e => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v)) setCastLayoutSettings({ craneExclusionIn: Math.max(120, Math.min(960, v)) });
                    }}
                    style={{ width: 64 }}
                  />
                  <span className="props-unit">in</span>
                </div>
              </div>
            </div>

            {/* Feature 4: simulation button */}
            <div className="props-section" style={{ marginTop: 8 }}>
              <button
                className="props-btn"
                onClick={runSimulation}
                disabled={simRunning || displayedPlaced.length === 0}
                style={{
                  width: '100%', padding: '6px 8px',
                  background: simRunning ? '#1a2a1a' : '#1a3a4a',
                  border: '1px solid #3a5a7a', borderRadius: 4,
                  color: simRunning ? '#668866' : '#4da6ff',
                  cursor: simRunning ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: 600,
                }}
              >
                {simRunning ? 'Running…' : 'Pick Simulation'}
              </button>
              {simError && (
                <div
                  role="alert"
                  style={{
                    marginTop: 6, padding: '6px 8px',
                    background: '#3a1a1a', border: '1px solid #7a3a3a', borderRadius: 4,
                    color: '#ffaaaa', fontSize: 11, lineHeight: 1.35,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>Simulation failed</div>
                  <div>{simError}</div>
                  <button
                    onClick={() => setSimError(null)}
                    style={{
                      marginTop: 4, padding: '2px 6px',
                      background: 'transparent', border: '1px solid #7a3a3a', borderRadius: 3,
                      color: '#ffcccc', cursor: 'pointer', fontSize: 10,
                    }}
                  >Dismiss</button>
                </div>
              )}
            </div>

            <div className="props-section" style={{ marginTop: 8 }}>
              <div className="props-label">{checkedIds.size} / {floorPanels.length} panels · {totalWeightTons.toFixed(1)} T total</div>
              {stackCount > 0 && (
                <div style={{ color: '#f0a030', fontSize: 11, marginTop: 4 }}>⚠ {stackCount} panels stacked</div>
              )}
              {panelOverrides.size > 0 && (
                <div style={{ fontSize: 11, marginTop: 4, color: '#88aacc' }}>
                  {panelOverrides.size} panel{panelOverrides.size !== 1 ? 's' : ''} manually moved
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
